import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../../components/ui/button';
import { LoadingButton } from '../../../components/common';
import { Input } from '../../../components/ui/input';
import { useChangeEmail, useVerifyEmail } from '../hooks/useSettings';

const STEPS = ['パスワード', 'メール', 'コード'];

const passwordSchema = z.object({
  password: z.string().min(1, 'パスワードを入力してください'),
});
type PasswordFormData = z.infer<typeof passwordSchema>;

const emailSchema = z.object({
  new_email: z.string().email('正しいメールアドレスを入力してください'),
  confirm_email: z.string().email('正しいメールアドレスを入力してください'),
}).refine(data => data.new_email === data.confirm_email, {
  message: '一致しません',
  path: ['confirm_email'],
});
type EmailFormData = z.infer<typeof emailSchema>;

const codeSchema = z.object({
  code: z.string().length(6, '6桁の確認コードを入力してください').regex(/^\d{6}$/, '数字6桁で入力してください'),
});
type CodeFormData = z.infer<typeof codeSchema>;

type Step = 'password' | 'email' | 'verify';

interface EmailChangeFlowProps {
  open: boolean;
  onClose: () => void;
}

const Stepper: React.FC<{ stepIndex: number }> = ({ stepIndex }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', minWidth: 0 }} data-testid="email-change-stepper">
    {STEPS.map((label, idx) => (
      <React.Fragment key={label}>
        {idx > 0 && (
          <div style={{
            flex: 1,
            height: '1px',
            background: idx <= stepIndex ? '#0a7a72' : '#E2E8F0',
            margin: '0 6px',
          }} />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 1, minWidth: 0 }}>
          <div style={{
            width: '26px',
            height: '26px',
            borderRadius: '50%',
            background: idx <= stepIndex ? '#0a7a72' : '#E2E8F0',
            color: idx <= stepIndex ? '#fff' : '#94A3B8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '12px',
            fontWeight: 700,
            flexShrink: 0,
          }}>
            {idx + 1}
          </div>
          <span style={{
            fontSize: '13px',
            fontWeight: idx === stepIndex ? 600 : 400,
            color: idx <= stepIndex ? '#0F172A' : '#94A3B8',
            whiteSpace: 'nowrap',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {label}
          </span>
        </div>
      </React.Fragment>
    ))}
  </div>
);

export const EmailChangeFlow: React.FC<EmailChangeFlowProps> = ({ open, onClose }) => {
  const [step, setStep] = useState<Step>('password');
  const [savedPassword, setSavedPassword] = useState('');
  const [savedNewEmail, setSavedNewEmail] = useState('');

  const stepIndex = step === 'password' ? 0 : step === 'email' ? 1 : 2;

  const { mutate: changeEmail, isPending: isChangePending } = useChangeEmail();
  const { mutate: verifyEmail, isPending: isVerifyPending } = useVerifyEmail();

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '' },
  });

  const emailForm = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: { new_email: '', confirm_email: '' },
  });

  const codeForm = useForm<CodeFormData>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const handleClose = () => {
    setStep('password');
    setSavedPassword('');
    setSavedNewEmail('');
    passwordForm.reset();
    emailForm.reset();
    codeForm.reset();
    onClose();
  };

  const onPasswordSubmit = (data: PasswordFormData) => {
    setSavedPassword(data.password);
    setStep('email');
  };

  const onEmailSubmit = (data: EmailFormData) => {
    const newEmail = data.new_email;
    setSavedNewEmail(newEmail);
    changeEmail(
      { password: savedPassword, newEmail },
      {
        onSuccess: (response) => {
          if (response.success) {
            setStep('verify');
          }
        },
      }
    );
  };

  const onCodeSubmit = (data: CodeFormData) => {
    verifyEmail(data.code, {
      onSuccess: (response) => {
        if (response.success) {
          handleClose();
        }
      },
    });
  };

  const handleResendCode = () => {
    changeEmail({ password: savedPassword, newEmail: savedNewEmail });
  };

  return (
    <>
      {open && step === 'password' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="password-confirm-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
          <div className="relative bg-white rounded-[16px] shadow-lg w-full max-w-sm mx-4 p-8 overflow-hidden">
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '16px' }}>メールアドレスの変更</h3>
            <Stepper stepIndex={stepIndex} />
            <p className="text-sm text-gray-500 mb-4">
              本人確認のため、現在のパスワードを入力してください。
            </p>
            <form id="password-form" onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}>
              <Controller
                name="password"
                control={passwordForm.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium mb-1">現在のパスワード</label>
                    <Input {...field} type="password" autoFocus aria-invalid={!!passwordForm.formState.errors.password} />
                    {passwordForm.formState.errors.password && (
                      <p className="text-sm text-red-500 mt-1">{passwordForm.formState.errors.password.message}</p>
                    )}
                  </div>
                )}
              />
            </form>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={handleClose}>キャンセル</Button>
              <LoadingButton form="password-form" type="submit" loading={isChangePending}>次へ</LoadingButton>
            </div>
          </div>
        </div>
      )}

      {open && step === 'email' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="email-change-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
          <div className="relative bg-white rounded-[16px] shadow-lg w-full max-w-sm mx-4 p-8 overflow-hidden">
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '16px' }}>メールアドレスの変更</h3>
            <Stepper stepIndex={stepIndex} />
            <p className="text-sm text-gray-500 mb-4">
              変更後のメールアドレスを入力してください。確認コードが送信されます。
            </p>
            <form id="email-form" noValidate onSubmit={emailForm.handleSubmit(onEmailSubmit)}>
              <Controller
                name="new_email"
                control={emailForm.control}
                render={({ field }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">新しいメールアドレス</label>
                    <Input {...field} type="email" data-testid="email-change-input" autoFocus aria-invalid={!!emailForm.formState.errors.new_email} />
                    {emailForm.formState.errors.new_email && (
                      <p className="text-sm text-red-500 mt-1">{emailForm.formState.errors.new_email.message}</p>
                    )}
                  </div>
                )}
              />
              <Controller
                name="confirm_email"
                control={emailForm.control}
                render={({ field }) => (
                  <div className="mb-4">
                    <label className="block text-sm font-bold mb-2">メールアドレス確認</label>
                    <Input {...field} type="email" placeholder="もう一度入力してください" data-testid="email-change-confirm-input" />
                    {emailForm.formState.errors.confirm_email && (
                      <p className="text-sm text-red-500 mt-1">{emailForm.formState.errors.confirm_email.message}</p>
                    )}
                  </div>
                )}
              />
            </form>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={() => setStep('password')}>戻る</Button>
              <LoadingButton form="email-form" type="submit" loading={isChangePending}>コードを送信</LoadingButton>
            </div>
          </div>
        </div>
      )}

      {open && step === 'verify' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="email-verify-dialog">
          <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
          <div className="relative bg-white rounded-[16px] shadow-lg w-full max-w-sm mx-4 p-8 overflow-hidden">
            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '16px' }}>メールアドレスの変更</h3>
            <Stepper stepIndex={stepIndex} />
            <h3 className="text-base font-semibold mb-2">確認コードの入力</h3>
            <p className="text-sm text-gray-500 mb-1 break-all">
              {savedNewEmail} に確認コードを送信しました。
            </p>
            <p className="text-sm text-gray-500 mb-4">24時間有効です。</p>
            <form id="code-form" onSubmit={codeForm.handleSubmit(onCodeSubmit)}>
              <Controller
                name="code"
                control={codeForm.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium mb-1">確認コード（6桁）</label>
                    <Input
                      {...field}
                      data-testid="email-verify-code-input"
                      maxLength={6}
                      autoFocus
                    />
                    {codeForm.formState.errors.code && (
                      <p className="text-sm text-red-500 mt-1">{codeForm.formState.errors.code.message}</p>
                    )}
                  </div>
                )}
              />
            </form>
            <div className="mt-2">
              <button
                type="button"
                className="text-sm text-primary hover:underline"
                data-testid="email-resend-code-link"
                onClick={handleResendCode}
                disabled={isChangePending}
              >
                {isChangePending ? '送信中...' : 'コードを再送信'}
              </button>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" type="button" onClick={handleClose}>キャンセル</Button>
              <LoadingButton form="code-form" type="submit" loading={isVerifyPending}>確認</LoadingButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
